import { BaseError, SyntaxError, SyntaxErrorProgram, UndefinedError } from "./errors.js";
import { commands } from "./commands.js";
import { printLine } from "./utilities.js";
import { state } from "./state.js";

export const BASIC_SETTINGS = {
    pc: 0,     // Program counter
    goto: null // GOTO line number
};

export let BASIC = {
    name: "BASIC",
    size: 0,
    lines: [],
    list: async function() {
        await listProgram(this.lines);
    },
    run: async function() {
        state.running = true;
        await runProgram(this.lines);
        state.running = false;
    }
};

const runProgram = async (lines) => {
    resetSettings(); // Reset the BASIC settings.
    
    try {
        let ic = 0; // Iteration counter

        while (BASIC_SETTINGS.pc < lines.length) {
            // **Yield control to the browser**  
            // This small delay allows the event loop to process pending events (like the ESC key)
            if (ic % 10 === 0) {
                await new Promise(resolve => setTimeout(resolve, 0));    
            } else {
                await Promise.resolve();
            }
            ic++;

            const line = lines[BASIC_SETTINGS.pc];

            // Check if the line is empty.
            if (line === null || line === undefined || line.trim().length === 0) {
                BASIC_SETTINGS.pc++;
                continue;
            }

            // Check if the program should be stopped. Break if the ESC key is pressed on the next command.
            if (!state.running) {
                await printLine("BREAK IN " + BASIC_SETTINGS.pc);
                break;
            }

            await executeLine(line, BASIC_SETTINGS.pc);
            
            // Check if a GOTO command was issued.
            if (BASIC_SETTINGS.goto !== null) {
                const target = BASIC_SETTINGS.goto;
                BASIC_SETTINGS.goto = null; // Reset the goto state.

                // Check if the line exists in the lines array.
                if (lines[target] === undefined || lines[target] === null) {
                    throw new UndefinedError(); // Line does not exist.
                }
            
                // If the line exists, update the program counter to jump to the target line.
                BASIC_SETTINGS.pc = target; // No need to decrement because we will increment it at the end of the loop.
            
                continue; // Skip the rest of the loop and jump to the target line.
            }
            // Increment the program counter.
            BASIC_SETTINGS.pc++;
        }
    }
    catch (error) {
        if (error instanceof SyntaxErrorProgram) {
            await error.handleError();
        } else if (error instanceof BaseError) {
            await error.handleError();
        } else {
            console.error(error);
            await new SyntaxError().handleError();
        }
    }
};

const resetSettings = () => {
    BASIC_SETTINGS.pc = 0; // Reset the program counter.
    BASIC_SETTINGS.goto = null; // Reset the GOTO line number.
};

const executeLine = async (line, lineNumber) => {
    line = line.trim();
    // Check if the line is empty.
    if (line.length === 0) {
        return;
    }

    // Get all command keys and sort them descending by length.
    // Sorting by length ensures that longer commands (e.g., PRINT#) are tried before shorter ones.
    const commandKeys = Object.keys(commands).sort((a, b) => b.length - a.length);

    let foundCommand = null;
    let args = "";

    // Try to match a command key at the very start of the input.
    for (const key of commandKeys) {
        // Compare in a case-insensitive manner.
        if (line.toUpperCase().startsWith(key.toUpperCase())) {
            // Command match found.
            foundCommand = key;

            // Extract the arguments by slicing the input after the command key.
            args = line.slice(key.length).trim();
            break;
        }
    }

    try {
        if (foundCommand) {
            // Execute the matched command with the extracted arguments.
            await commands[foundCommand](args);
        } else {
            // If no command is found, handle it as a syntax error.
            throw new SyntaxError();
        }
    }
    catch (error) {
        throw new SyntaxErrorProgram(error.message, lineNumber);
    }
};

const listProgram = async (lines) => {
    // Loop through all lines in the custom program and print them.
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line === null || line === undefined) {
            continue;
        }
        await printLine(i + " " + line);
    }
};